import React, { useState, useEffect, useRef } from 'react';
import { Download, FileText, FileType, FileSpreadsheet } from 'lucide-react';

const ExportDropdown = ({ 
  eventId = null, 
  label = "Download Report", 
  variant = "primary", 
  handleDownloadEventReport, 
  handleDownloadReport, 
  eventSpecificLoading, 
  eventPayLoading 
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownDirection, setDropdownDirection] = useState('down'); // 'down' or 'up'
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const handleExport = (format) => {
    if (eventId) {
      handleDownloadEventReport(eventId, format);
    } else {
      handleDownloadReport(format);
    }
    setShowDropdown(false);
  };

  const isLoading = eventId ? eventSpecificLoading : eventPayLoading;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 320; // Approximate height of dropdown
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Determine if dropdown should open upward or downward
      let topPosition;
      let direction = 'down';
      
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        // Open upward if not enough space below
        topPosition = rect.top - dropdownHeight - 8;
        direction = 'up';
      } else {
        // Open downward normally
        topPosition = rect.bottom + 8;
        direction = 'down';
      }
      
      // Ensure dropdown doesn't go off-screen horizontally
      let leftPosition = rect.right - 256; // 256px is dropdown width
      if (leftPosition < 10) {
        leftPosition = 10; // Prevent going off left edge
      }
      if (leftPosition + 256 > window.innerWidth - 10) {
        leftPosition = window.innerWidth - 266; // Prevent going off right edge
      }
      
      setDropdownPosition({
        top: Math.max(10, Math.min(topPosition, window.innerHeight - dropdownHeight - 10)),
        left: leftPosition,
      });
      setDropdownDirection(direction);
    }
  }, [showDropdown]);

  const handleButtonClick = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleDropdownItemClick = (e, format) => {
    e.stopPropagation();
    handleExport(format);
  };

  return (
    <div className="relative inline-block" ref={buttonRef}>
      <button
        onClick={handleButtonClick}
        disabled={isLoading}
        className={`inline-flex items-center px-4 py-2.5 ${
          variant === 'primary'
            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
            : 'bg-white text-blue-600 border border-blue-300'
        } rounded-xl font-medium hover:opacity-90 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Loading...
          </>
        ) : (
          <>
            <Download size={20} className="mr-2" />
            {label}
          </>
        )}
      </button>

      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Dropdown - using fixed positioning */}
          <div
            ref={dropdownRef}
            className={`fixed z-50 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden ${
              dropdownDirection === 'up' ? 'origin-bottom' : 'origin-top'
            }`}
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              maxHeight: 'min(400px, calc(100vh - 20px))',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4">
              <div className="mb-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Download Payment Report</h4>
                <p className="text-xs text-gray-500">
                  {eventId ? 'Event-specific report' : 'All events report'}
                </p>
              </div>

              <div className="space-y-2">
                <button
                  onClick={(e) => handleDropdownItemClick(e, 'pdf')}
                  disabled={isLoading}
                  className="flex items-center w-full px-4 py-3.5 text-sm text-gray-700 hover:bg-blue-50 rounded-xl transition-all duration-200 group border border-gray-100 hover:border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center mr-3 group-hover:bg-red-100 transition-colors flex-shrink-0">
                    <FileText className="text-red-600" size={18} />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">PDF Document</div>
                    <div className="text-xs text-gray-500 mt-0.5">Best for printing and sharing</div>
                  </div>
                </button>

                <button
                  onClick={(e) => handleDropdownItemClick(e, 'csv')}
                  disabled={isLoading}
                  className="flex items-center w-full px-4 py-3.5 text-sm text-gray-700 hover:bg-blue-50 rounded-xl transition-all duration-200 group border border-gray-100 hover:border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center mr-3 group-hover:bg-green-100 transition-colors flex-shrink-0">
                    <FileType className="text-green-600" size={18} />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">CSV File</div>
                    <div className="text-xs text-gray-500 mt-0.5">Spreadsheet format</div>
                  </div>
                </button>

                <button
                  onClick={(e) => handleDropdownItemClick(e, 'excel')}
                  disabled={isLoading}
                  className="flex items-center w-full px-4 py-3.5 text-sm text-gray-700 hover:bg-blue-50 rounded-xl transition-all duration-200 group border border-gray-100 hover:border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center mr-3 group-hover:bg-green-100 transition-colors flex-shrink-0">
                    <FileSpreadsheet className="text-green-600" size={18} />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">Excel Workbook</div>
                    <div className="text-xs text-gray-500 mt-0.5">For data analysis and editing</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ExportDropdown;